import { useMemo, useState } from "react";
import { ShieldAlert, Trash2 } from "lucide-react";
import { useOrganization } from "../organization/useOrganization";
import { useSales } from "../sales/useSales";
import { useScheduling } from "../scheduling/useScheduling";
import { useIncentives } from "../incentives/useIncentives";
import { useShop } from "../shop/useShop";
import { Button } from "../../shared/ui/Button";
import { Card } from "../../shared/ui/Card";
import { PageSkeleton, StatusBadge } from "../../shared/ui/Status";
import { useGovernance, useGovernanceActions } from "./useGovernance";

type DeleteType =
  | "account"
  | "course_product"
  | "venue"
  | "schedule"
  | "energy_rule"
  | "energy_claim"
  | "shop_product"
  | "announcement";
type Option = { id: string; label: string };

const labels: Record<DeleteType, string> = {
  account: "会员、员工或店长账号",
  course_product: "课程产品",
  venue: "场地或工位",
  schedule: "未完成排课",
  energy_rule: "星能申报规则",
  energy_claim: "待审批星能申报",
  shop_product: "商城商品",
  announcement: "公告",
};

export function SuperDeleteCenter() {
  const organization = useOrganization();
  const sales = useSales();
  const scheduling = useScheduling();
  const incentives = useIncentives();
  const shop = useShop();
  const governance = useGovernance();
  const actions = useGovernanceActions();
  const [type, setType] = useState<DeleteType>("account");
  const [targetId, setTargetId] = useState("");
  const [reason, setReason] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const options = useMemo<Record<DeleteType, Option[]>>(
    () => ({
      account:
        organization.data?.accounts
          .filter((item) => item.role !== "super" && item.status === "active")
          .map((item) => ({
            id: item.id,
            label: `${item.name} · ${roleLabel(item.role)} · ${item.phone}`,
          })) ?? [],
      course_product:
        sales.data?.products
          .filter((item) => item.active)
          .map((item) => ({ id: item.id, label: item.name })) ?? [],
      venue:
        scheduling.data?.venues
          .filter((item) => item.active)
          .map((item) => ({ id: item.id, label: item.name })) ?? [],
      schedule:
        scheduling.data?.schedules
          .filter((item) =>
            ["scheduled", "checked_in"].includes(item.status),
          )
          .map((item) => ({
            id: item.id,
            label: `${new Date(item.startAt).toLocaleString("zh-CN", { hour12: false })} · ${item.status === "checked_in" ? "已签到" : "已排课"}`,
          })) ?? [],
      energy_rule:
        incentives.data?.rules
          .filter((item) => item.active)
          .map((item) => ({
            id: item.id,
            label: `${item.name} · +${item.points}`,
          })) ?? [],
      energy_claim:
        incentives.data?.claims
          .filter((item) => item.status === "pending")
          .map((item) => ({ id: item.id, label: item.evidence })) ?? [],
      shop_product:
        shop.data?.products
          .filter((item) => item.active)
          .map((item) => ({
            id: item.id,
            label: `${item.name} · 库存${item.stock}`,
          })) ?? [],
      announcement:
        governance.data?.announcements
          .filter((item) => item.active)
          .map((item) => ({ id: item.id, label: item.title })) ?? [],
    }),
    [
      governance.data,
      incentives.data,
      organization.data,
      sales.data,
      scheduling.data,
      shop.data,
    ],
  );

  if (
    !organization.data ||
    !sales.data ||
    !scheduling.data ||
    !incentives.data ||
    !shop.data ||
    !governance.data
  )
    return <PageSkeleton />;

  const submit = async () => {
    const target = options[type].find((item) => item.id === targetId);
    if (!target) return setError("请选择需要删除的数据。");
    if (reason.trim().length < 2) return setError("请填写至少2个字的删除原因。");
    if (
      !window.confirm(
        `确认删除“${target.label}”？有关联历史的数据将安全停用、取消或驳回，不会破坏账务与审计。`,
      )
    )
      return;
    setError("");
    setNotice("");
    try {
      const result = await actions.safeDelete({
        entityType: type,
        targetId,
        reason,
      });
      setTargetId("");
      setReason("");
      setNotice(`处理完成：${modeLabel(result.mode)}。历史流水与审计已保留。`);
    } catch (cause) {
      setError(cause instanceof Error ? friendly(cause.message) : "删除失败。");
    }
  };

  return (
    <>
      <div className="section-divider">
        <span>SUPER ADMIN · SAFE DELETE</span>
        <h2>全局数据删除治理</h2>
      </div>
      <Card className="management-card super-delete-center">
        <div className="card-heading">
          <ShieldAlert />
          <div>
            <span>仅超管 · 强审计 · 自动防冲突</span>
            <h2>删除、停用或归档任意业务主数据</h2>
          </div>
          <StatusBadge tone="danger">高风险操作</StatusBadge>
        </div>
        <p className="muted-copy">
          未被引用的场地会物理删除；账号、产品、规则、商品和公告执行停用；未完成排课执行取消；待审批申报执行驳回。已经完成的订单、课消、星能流水和审计属于财务历史，禁止抹除。
        </p>
        {(notice || error) && (
          <div
            className={
              error ? "inline-notice inline-notice--error" : "inline-notice"
            }
          >
            {error || notice}
          </div>
        )}
        <div className="v4-form">
          <label className="v4-field">
            <span>数据类型</span>
            <select
              value={type}
              onChange={(event) => {
                setType(event.target.value as DeleteType);
                setTargetId("");
              }}
            >
              {(Object.keys(labels) as DeleteType[]).map((key) => (
                <option key={key} value={key}>
                  {labels[key]}
                </option>
              ))}
            </select>
          </label>
          <label className="v4-field">
            <span>选择数据</span>
            <select
              value={targetId}
              onChange={(event) => setTargetId(event.target.value)}
            >
              <option value="">
                {options[type].length ? "请选择" : "当前没有可删除数据"}
              </option>
              {options[type].map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className="v4-field">
            <span>删除原因（写入审计）</span>
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="例如：重复测试数据、场地已撤销"
            />
          </label>
          <Button
            variant="danger"
            disabled={actions.pending || !targetId}
            onClick={submit}
          >
            <Trash2 size={16} />
            确认安全删除
          </Button>
        </div>
      </Card>
    </>
  );
}

function roleLabel(role: string) {
  return { member: "会员", employee: "员工", manager: "店长" }[role] ?? role;
}
function modeLabel(mode: string) {
  return {
    deleted: "已物理删除",
    disabled: "账号已停用",
    archived: "已停用并归档",
    cancelled: "排课已取消",
    rejected: "申报已驳回",
  }[mode] ?? "已安全处理";
}
function friendly(message: string) {
  if (message.includes("HISTORICAL_RECORD_PROTECTED"))
    return "该记录已经形成历史流水，不能抹除；请通过退款、冲正或撤销流程处理。";
  if (message.includes("PROTECTED_SUPER_ACCOUNT"))
    return "超管账号受保护，不能删除本人或其他超管。";
  if (message.includes("DELETE_REASON_REQUIRED"))
    return "请填写删除原因。";
  return message;
}
